/**
 * Ханшийн baseline график — зурах давхарга. Цэвэр SVG.
 *
 * BASELINE ГЭЖ ЮУ ВЭ
 *  Мужийн ЭХНИЙ ханшийг лавлах шугам болгож, түүнээс дээших талбайг
 *  ягаан (`alert`), доорхыг оюу (`accent`) будна. «Аялал эхлэхэд
 *  байсантай харьцуулбал рубль өдийд хэр байна вэ» гэдгийг нэг
 *  харцаар өгнө. Trading-ийн baseline chart-ийн зарчим.
 *
 * ⚠️ ӨНГӨНИЙ УТГА: ханш ӨСӨХ нь импортлогчид МУУ (гутал үнэтэй болно).
 *    Тиймээс дээш = ягаан, доош = оюу. Хөрөнгийн биржийн «өсөлт =
 *    ногоон» зуршлаар будвал захирал эсрэгээр нь уншина.
 *
 * ⚠️ ӨНГӨ ГАНЦААРАА УТГА ЗӨӨХГҮЙ. Оюу (#0f7d77) ба ягаан (#a8005c)
 *    хоёрын deuteran ялгаа ΔE 5.4 — улаан-ногоон сохор хүн ялгахгүй
 *    (dataviz validator, 2026-08-25). Тиймээс туйл нь ГУРВАН давхар
 *    кодлогдсон: (1) лавлах шугамаас ДЭЭШ/ДООШ байрлал, (2) ▲/▼
 *    сум, (3) тэмдэгтэй тоо. Өнгө нь зөвхөн бататгана.
 *
 * ⚠️ ШУЛУУН ХЭРЧИМ, муруй БИШ. Монголбанк өдөрт нэг ханш зарладаг —
 *    хоёр өдрийн хооронд юу болсныг БИД МЭДЭХГҮЙ. Муруй татвал
 *    байхгүй өдрийн доторх хөдөлгөөнийг зохиох болно.
 *
 * ⚠️ Дугуй, шошго, үнийн шошгыг энд зурахгүй — `preserveAspectRatio="none"`
 *    нь `<circle>`-ийг зууван, `<text>`-ийг сунгадаг. Тэдгээр нь
 *    `fx-card.tsx` дотор HTML болж, хувиар байрлана.
 */

const W = 1000;
const H = 260;
/** Дээд/доод зай — оргил нь хүрээнд наалдаж таслагдсан мэт харагдахгүй. */
const PAD = 24;

export interface FxGeometry {
  /** 0–100 хувь: цэг бүрийн байрлал (HTML давхарга үүнийг ашиглана) */
  points: { xPct: number; yPct: number }[];
  /** Лавлах шугамын босоо байрлал, 0–100 хувь */
  baselinePct: number;
  lo: number;
  hi: number;
  /** Тэнхлэгийн зураас — утга ба 0–100 хувь */
  ticks: { value: number; yPct: number }[];
}

/**
 * Масштаб. Лавлах утгыг ЗААВАЛ багтаана — эс тэгвээс бүх өгөгдөл
 * лавлахаас дээгүүр байхад лавлах шугам хүрээнээс гарч, будалт
 * тайлбаргүй болно.
 */
export function fxGeometry(values: number[], baseline: number): FxGeometry {
  const lo = Math.min(...values, baseline);
  const hi = Math.max(...values, baseline);
  const span = hi - lo || 0.1;
  const yLo = lo - span * 0.18;
  const yHi = hi + span * 0.18;

  const toPct = (v: number) => ((yHi - v) / (yHi - yLo)) * 100;

  return {
    points: values.map((v, i) => ({
      xPct: values.length > 1 ? (i / (values.length - 1)) * 100 : 50,
      yPct: toPct(v),
    })),
    baselinePct: toPct(baseline),
    lo,
    hi,
    // Дөрвөн зураас — түүнээс олон бол жижиг картанд бөөгнөрнө
    ticks: [0, 1, 2, 3].map((k) => {
      const value = yHi - ((yHi - yLo) * k) / 3;
      return { value, yPct: toPct(value) };
    }),
  };
}

/** Хувийг viewBox-ийн координат болгоно. */
const vx = (pct: number) => (pct / 100) * W;
const vy = (pct: number) => PAD + (pct / 100) * (H - PAD * 2);

export function FxPlot({
  values,
  geometry,
  hover,
  onHover,
  label,
}: {
  values: number[];
  geometry: FxGeometry;
  hover: number | null;
  onHover: (i: number | null) => void;
  label: string;
}) {
  const { points, baselinePct, ticks } = geometry;
  const baseY = vy(baselinePct);

  const line = points
    .map((p, i) => `${i ? 'L' : 'M'}${vx(p.xPct).toFixed(2)} ${vy(p.yPct).toFixed(2)}`)
    .join(' ');

  // Шугам ба лавлах шугамын ХООРОНДОХ талбай. Ёроол руу биш, лавлах
  // руу хаана — ингэснээр нэг замыг хоёр тийш нь тайрахад дээд/доод
  // хэсэг өөрөө ялгарна.
  const band = points.length
    ? `${line} L${vx(points.at(-1)!.xPct).toFixed(2)} ${baseY.toFixed(2)} L${vx(points[0]!.xPct).toFixed(2)} ${baseY.toFixed(2)} Z`
    : '';

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="h-full w-full touch-pan-y"
      role="img"
      aria-label={label}
      onPointerLeave={() => onHover(null)}
      onPointerMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        if (r.width === 0 || values.length < 2) return;
        const i = Math.round(((e.clientX - r.left) / r.width) * (values.length - 1));
        onHover(Math.min(values.length - 1, Math.max(0, i)));
      }}
    >
      <defs>
        {/* Дүүргэлт лавлах шугамаас ХОЛДОХ тусам гүнзгийрнэ */}
        <linearGradient id="fx-up" gradientUnits="userSpaceOnUse" x1="0" y1={PAD} x2="0" y2={baseY}>
          <stop offset="0%" stopColor="var(--color-alert)" stopOpacity="0.34" />
          <stop offset="100%" stopColor="var(--color-alert)" stopOpacity="0.04" />
        </linearGradient>
        <linearGradient id="fx-dn" gradientUnits="userSpaceOnUse" x1="0" y1={baseY} x2="0" y2={H - PAD}>
          <stop offset="0%" stopColor="var(--color-accent)" stopOpacity="0.04" />
          <stop offset="100%" stopColor="var(--color-accent)" stopOpacity="0.34" />
        </linearGradient>

        <clipPath id="fx-clip-up">
          <rect x="0" y="0" width={W} height={Math.max(0, baseY)} />
        </clipPath>
        <clipPath id="fx-clip-dn">
          <rect x="0" y={baseY} width={W} height={Math.max(0, H - baseY)} />
        </clipPath>
      </defs>

      {/* Тэнхлэгийн зураас — бүтэн шугам, тасархай БИШ (тасархай нь
          «таамаг» эсвэл «босго» гэж уншигддаг) */}
      {ticks.map((t) => (
        <line
          key={t.value}
          x1="0"
          x2={W}
          y1={vy(t.yPct)}
          y2={vy(t.yPct)}
          stroke="var(--color-hair)"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
          opacity="0.5"
        />
      ))}

      {/* Дүүргэлт */}
      <g clipPath="url(#fx-clip-up)">
        <path d={band} fill="url(#fx-up)" />
      </g>
      <g clipPath="url(#fx-clip-dn)">
        <path d={band} fill="url(#fx-dn)" />
      </g>

      {/* Лавлах шугам — саармаг өнгө. Хоёр туйлын дунд «юу ч биш»
          гэдгийг илэрхийлэх ёстой тул өнгө хэрэглэхгүй. */}
      <line
        x1="0"
        x2={W}
        y1={baseY}
        y2={baseY}
        stroke="var(--color-ink-3)"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
        opacity="0.7"
      />

      {/* Шугам — лавлахаас дээш/доош өөр өнгөтэй */}
      <g clipPath="url(#fx-clip-up)">
        <path
          d={line}
          fill="none"
          stroke="var(--color-alert)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </g>
      <g clipPath="url(#fx-clip-dn)">
        <path
          d={line}
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </g>

      {/* Хулганы босоо шугам */}
      {hover !== null && points[hover] ? (
        <line
          x1={vx(points[hover]!.xPct)}
          x2={vx(points[hover]!.xPct)}
          y1={PAD}
          y2={H - PAD}
          stroke="var(--color-ink-3)"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
          opacity="0.8"
        />
      ) : null}
    </svg>
  );
}

/** Хувиас CSS байрлал — HTML давхаргад цэг, шошго тавихад. */
export function atPoint(p: { xPct: number; yPct: number }) {
  // SVG-ийн PAD-ыг HTML тал давтана: дээр/доор PAD/H хувийг үлдээнэ
  const inset = (PAD / H) * 100;
  return {
    left: `${p.xPct}%`,
    top: `${inset + (p.yPct / 100) * (100 - inset * 2)}%`,
  };
}

/** Тэнхлэгийн зурааст ижил хувиргалт — үнийн шошго, tick label-д. */
export function atValue(yPct: number) {
  const inset = (PAD / H) * 100;
  return `${inset + (yPct / 100) * (100 - inset * 2)}%`;
}
